'use client';
import React, { useState, useEffect, useRef } from "react";
import TabContent from "./tabContent"; // Same as before
import ProfileInfo from "./profileInfo"; // New component for the right side
import CrossButton from "../../assets/profileModal/crossButtonComponent"; // Import the cross button component

export default function ProfileCard({ closeModal, userData, tokenId }) {
  const [activeTab, setActiveTab] = useState("userInfo");
  const [modalVisible, setModalVisible] = useState(true);
  const [profileInfoVisible, setProfileInfoVisible] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false); // Track if it's a mobile view
  const [showProfileFirst, setShowProfileFirst] = useState(true); // Flag to toggle profile info and tab content

  const modalRef = useRef(null); // Reference for the modal container

  const tabs = [
    { name: "User Info", value: "userInfo" },
    { name: "Help", value: "help" },
    { name: "Billing", value: "billing" },
  ];

  useEffect(() => {
    // Detect screen size changes
    const handleResize = () => {
      if (window.innerWidth < 768) { // md breakpoint
        setIsMobileView(true);
      } else {
        setIsMobileView(false);
      }
    };

    // Initialize screen size on load
    handleResize();

    // Add event listener to listen for screen size changes
    window.addEventListener('resize', handleResize);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => setProfileInfoVisible(true), 100); // Delay to animate profile info
    return () => clearTimeout(timeoutId);
  }, []);

  // Close modal on click outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setModalVisible(false);
        setTimeout(() => closeModal(), 300); // Wait for the transition before calling closeModal
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [closeModal]);

  const handleCloseModal = () => {
    setModalVisible(false);
    setTimeout(() => closeModal(), 300); // Wait for the transition before calling closeModal
  };

  const handleToggleContent = () => {
    setShowProfileFirst((prevState) => !prevState);
  };

  return (
    <div className={`profile_card-modal ${modalVisible ? "show" : ""} border-[2px]`}>
      <div className="fixed inset-0 bg-white bg-opacity-95 z-50 flex justify-center items-center">
        {/* Modal Content */}
        <div
          ref={modalRef}
          className="relative bg-white rounded-lg shadow-lg max-w-5xl w-full h-[700px] p-8 flex flex-col"
        >
          <h2 className="text-2xl font-semibold mb-6">My Account</h2>

          <div className="flex h-full flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-8">
            {/* Left Sidebar Section (on desktop) */}
            {!isMobileView && (
              <div className="w-1/2 flex flex-col h-full">
                {/* Tabs Section */}
                <div className="mb-4">
                  <ul className="flex space-x-1 lg:space-x-6 whitespace-nowrap">
                    {tabs.map((tab) => (
                      <li key={tab.value}>
                        <button
                          onClick={() => setActiveTab(tab.value)}
                          className={`profile_card-tab text-base lg:text-lg font-medium pb-2 px-2 lg:px-4 ${
                            activeTab === tab.value
                              ? "profile_card-tab-active"
                              : "profile_card-tab-inactive"
                          }`}
                        >
                          {tab.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Tab Content Section */}
                <div className="relative flex-1"> {/* Allows scrolling in case content exceeds available space */}
                  <TabContent tokenId={tokenId} activeTab={activeTab} userData={userData} />
                </div>
              </div>
            )}

            {/* Right Section (ProfileInfo) */}
            <div
              className={`w-full md:w-1/2 bg-gray-50 p-4 rounded-lg flex flex-col justify-center items-center profile_card-profile-info ${profileInfoVisible ? "show" : ""}`}
            >
              {showProfileFirst && <ProfileInfo userData={userData} tokenId={tokenId}/>}
            </div>

            {/* Mobile View (when screen is small) */}
            {isMobileView && (
              <div className="w-full flex flex-col space-y-4">
                {/* Toggle Button (styled as a sidebar toggle) */}
                <button
                  onClick={handleToggleContent}
                  className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 px-4 py-2 bg-blue-500 text-white rounded-full text-lg shadow-lg z-10"
                >
                  <span className="sr-only">Toggle Profile / Tab Content</span>
                  {/* You can use a hamburger icon or something similar here */}
                  <span className="block w-6 h-0.5 bg-white my-1"></span>
                  <span className="block w-6 h-0.5 bg-white my-1"></span>
                  <span className="block w-6 h-0.5 bg-white my-1"></span>
                </button>

                {/* Conditionally render ProfileInfo or TabContent */}
                {showProfileFirst ? (
                  <div className="profile_card-profile-info mb-4">
                    <ProfileInfo userData={userData} tokenId={tokenId} />
                  </div>
                ) : (
                  <div className="profile_card-tab-content max-sm:min-h-[100vh]">
                    <div className="mb-4">
                      <select
                        onChange={(e) => setActiveTab(e.target.value)}
                        value={activeTab}
                        className="px-4 py-2 border border-gray-300 rounded-md"
                      >
                        {tabs.map((tab) => (
                          <option key={tab.value} value={tab.value}>
                            {tab.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="relative flex-1">
                      <TabContent tokenId={tokenId} activeTab={activeTab} userData={userData}/>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Close Button */}
          {isMobileView ? "" : (
            <div className={`absolute top-0 right-0 profile_card-close-btn ${modalVisible ? "show" : ""}`}>
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 text-black hover:text-[#0062ff] hover:bg-opacity-50 rounded-md px-2 py-1"
              >
                <CrossButton height="8px" width="8px" fill="#0062ff" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
